import React, {
  useState, useEffect, useMemo, useRef,
} from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import getSentEmail from '../../../api/graphql/queries/getSentEmail';
import LoadIcon from '../../LoadIcon';
import {
  statusTone, statusLabel, withCsp, buildTimeline, redactionNote,
} from '../../../utils/sentEmails';

const formatWhen = (value, pattern) => {
  if (!value) return '—';
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format(pattern || 'DD MMM YYYY · HH:mm') : String(value);
};

const METADATA_ONLY_REASONS = {
  'too-large': 'it was too large to archive',
};

// Detail drawer for one archived email: Loyalty's prize drawer (.pd-scrim →
// aside.pd-panel, Escape closes, loading / failed / loaded states) holding an
// email instead of a prize.
//
// The header is drawn from the list row straight away and upgraded to the
// fetched record when it lands, so the drawer never opens blank.
//
// The body renders in <iframe sandbox="" srcDoc>: no scripts, no forms, no
// same-origin access, no top navigation. withCsp() adds a CSP that blocks every
// network load except images from our own asset hosts (so tracking pixels never
// fire), and strips <base>, meta http-equiv and link targets, which CSP does not cover.
const SentEmailDrawer = ({ row, onClose }) => {
  const [result, setResult] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [view, setView] = useState('html');
  const closeRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setResult(null);
    getSentEmail(row.messageId).then((data) => {
      if (cancelled) return;
      setResult(data);
      setIsLoading(false);
    });
    return () => { cancelled = true; };
  }, [row.messageId]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Move focus into the dialog, and hand it back to whatever opened it (the table
  // row) on close, so keyboard users land where they were.
  useEffect(() => {
    const opener = typeof document !== 'undefined' ? document.activeElement : null;
    if (closeRef.current) closeRef.current.focus();
    return () => {
      if (opener && typeof opener.focus === 'function') opener.focus();
    };
  }, []);

  const email = result && result.isAuth && !result.error ? result.email : null;

  // Default to the HTML part whenever there is one.
  useEffect(() => {
    if (email) setView(email.html ? 'html' : 'text');
  }, [email]);

  const srcDoc = useMemo(() => (email && email.html ? withCsp(email.html) : ''), [email]);

  const shown = email || row;
  const stopPropagation = event => event.stopPropagation();

  let body;
  if (isLoading) {
    body = (
      <div className="pd-sec" style={{ textAlign: 'center' }}>
        <LoadIcon />
      </div>
    );
  } else if (!result) {
    body = (
      <div className="pd-sec">
        <div className="banner bad">Unable to load this email. Close the drawer and try again.</div>
      </div>
    );
  } else if (!result.isAuth) {
    body = (
      <div className="pd-sec">
        <div className="banner bad">You are not authorized to view sent emails.</div>
      </div>
    );
  } else if (result.error === 'not_configured') {
    body = (
      <div className="pd-sec">
        <div className="banner info">Sent emails isn&apos;t set up for this environment yet.</div>
      </div>
    );
  } else if (result.error) {
    body = (
      <div className="pd-sec">
        <div className="banner bad">{`Unable to load this email (${result.error}).`}</div>
      </div>
    );
  } else if (!email) {
    body = (
      <div className="pd-sec">
        <div className="pd-empty">This email is no longer in the archive.</div>
      </div>
    );
  } else {
    const timeline = buildTimeline(email);
    const redacted = redactionNote(email.redactions);
    const isMetadataOnly = email.archiveState === 'metadata-only';
    const hasHtml = !!email.html;
    const hasText = !!email.text;
    const reason = METADATA_ONLY_REASONS[email.metadataOnlyReason] || email.metadataOnlyReason;

    let content;
    if (isMetadataOnly) {
      content = (
        <div className="banner info">
          {`Only this email's details were archived${reason ? `, because ${reason}` : ''}. The body isn't available.`}
        </div>
      );
    } else if (!hasHtml && !hasText) {
      content = <div className="pd-empty">No body was archived for this email.</div>;
    } else if (view === 'html' && hasHtml) {
      content = (
        <iframe
          className="bodyframe"
          sandbox=""
          srcDoc={srcDoc}
          title={`Archived email: ${email.subject || email.messageId}`}
          referrerPolicy="no-referrer"
        />
      );
    } else {
      content = <pre className="bodytext">{email.text || ''}</pre>;
    }

    body = (
      <>
        <div className="pd-sec">
          <h4>
            Timeline
            <em>SES events</em>
          </h4>
          <ol className="timeline">
            {timeline.map((entry, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <li key={`${entry.type}-${entry.at}-${index}`}>
                <span className="t">{formatWhen(entry.at, 'DD MMM YYYY · HH:mm:ss')}</span>
                <span className={`chip chip--${statusTone(entry.type)}`}>{statusLabel(entry.type)}</span>
                <span className="d">{entry.detail}</span>
              </li>
            ))}
          </ol>
        </div>

        <div className="pd-sec">
          <h4>
            Body
            {!isMetadataOnly && (hasHtml || hasText) ? (
              <span className="viewtoggle" role="group" aria-label="Body format">
                <button
                  type="button"
                  className={view === 'html' ? 'on' : ''}
                  aria-pressed={view === 'html'}
                  disabled={!hasHtml}
                  onClick={() => setView('html')}
                >
                  HTML
                </button>
                <button
                  type="button"
                  className={view === 'text' ? 'on' : ''}
                  aria-pressed={view === 'text'}
                  disabled={!hasText}
                  onClick={() => setView('text')}
                >
                  Text
                </button>
              </span>
            ) : null}
          </h4>
          {!isMetadataOnly && (redacted || (view === 'html' && hasHtml)) ? (
            <p className="bodynote">
              {redacted ? `[${redacted}] ` : ''}
              {view === 'html' && hasHtml ? 'Only our own image hosts load here; tracking pixels and other remote content are blocked, and links are disabled.' : ''}
            </p>
          ) : null}
          {content}
        </div>
      </>
    );
  }

  return (
    <div className="pd-scrim" onClick={onClose}>
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
      <aside
        className="pd-panel"
        role="dialog"
        aria-modal="true"
        aria-label={shown.subject || 'Sent email'}
        onClick={stopPropagation}
      >
        <div className="pd-top">
          <button type="button" className="x" onClick={onClose} aria-label="Close" ref={closeRef}>✕</button>
          <h2>{shown.subject || '(no subject)'}</h2>
          <div className="sub">
            <span className={`kind ${shown.dataClass || ''}`}>{shown.kind || '—'}</span>
            <span className={`chip chip--${statusTone(shown.status)}`}>{statusLabel(shown.status)}</span>
          </div>
          <dl className="pd-meta">
            <dt>From</dt>
            <dd>{shown.from || '—'}</dd>
            <dt>To</dt>
            <dd>{shown.recipient || '—'}</dd>
            <dt>Sent at</dt>
            <dd title={shown.sentAt || ''}>{formatWhen(shown.sentAt, 'DD MMM YYYY · HH:mm:ss')}</dd>
            {email && email.templateId ? (
              <>
                <dt>Template</dt>
                <dd>{email.templateId}</dd>
              </>
            ) : null}
            <dt>Message id</dt>
            <dd>{shown.messageId}</dd>
          </dl>
        </div>

        {body}

        <div className="pd-foot">
          Read-only · every view is logged
        </div>
      </aside>
    </div>
  );
};

SentEmailDrawer.propTypes = {
  row: PropTypes.shape({
    messageId: PropTypes.string.isRequired,
    sentAt: PropTypes.string,
    recipient: PropTypes.string,
    from: PropTypes.string,
    subject: PropTypes.string,
    kind: PropTypes.string,
    dataClass: PropTypes.string,
    status: PropTypes.string,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
};

export default SentEmailDrawer;
